import { useEffect, useMemo, useState } from 'react';

import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import FingerprintIcon from '@mui/icons-material/Fingerprint';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import ListIcon from '@mui/icons-material/List';
import PercentIcon from '@mui/icons-material/Percent';
import ScheduleIcon from '@mui/icons-material/Schedule';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import Autocomplete from '@mui/material/Autocomplete';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';

import { EnumMetadataModel, ParameterValueOption, PrimitiveType } from '../../../../api/api';
import { ConstExpressionModel } from '../../Models/ConstExpressionModel';
import { ExpressionModel } from '../../Models/ExpressionModel';
import { ExpressionTypes } from '../../Models/ExpressionTypes';
import { getValueOptionLabel, hasValueOption } from '../../Services/parameterValueOptions';
import GuidInput from '../Inputs/GuidInput';
import TimeSpanInput from '../Inputs/TimeSpanInput';

const SAVE_CONST_VALUE_TYPES: PrimitiveType[] = [PrimitiveType.Guid, PrimitiveType.DateTime, PrimitiveType.TimeSpan, PrimitiveType.Guid];

const SUPPORTED_CONST_INPUT_TYPES: PrimitiveType[] = [
	PrimitiveType.String,
	PrimitiveType.Enum,
	PrimitiveType.Integer,
	PrimitiveType.Decimal,
	PrimitiveType.Boolean,
	PrimitiveType.Guid,
	PrimitiveType.TimeSpan,
	PrimitiveType.Date,
	PrimitiveType.DateTime,
];

function getIconForType(type: PrimitiveType): React.ReactNode {
	switch (type) {
		case PrimitiveType.String:
			return <TextFieldsIcon fontSize="small" />;
		case PrimitiveType.Integer:
			return <FormatListNumberedIcon fontSize="small" />;
		case PrimitiveType.Decimal:
			return <PercentIcon fontSize="small" />;
		case PrimitiveType.Boolean:
			return <CheckBoxIcon fontSize="small" />;
		case PrimitiveType.DateTime:
			return <ScheduleIcon fontSize="small" />;
		case PrimitiveType.Date:
			return <CalendarTodayIcon fontSize="small" />;
		case PrimitiveType.TimeSpan:
			return <AccessTimeIcon fontSize="small" />;
		case PrimitiveType.Guid:
			return <FingerprintIcon fontSize="small" />;
		case PrimitiveType.Enum:
			return <ListIcon fontSize="small" />;
		default:
			return null;
	}
}

function DetectValueType(expression: ExpressionModel | undefined): PrimitiveType | undefined {
	if (expression && expression.Type === ExpressionTypes.Const) {
		const valueType = (expression as ConstExpressionModel).ValueType;
		if (valueType) {
			return valueType;
		}
		if (expression?.Value !== undefined) {
			if (typeof expression.Value === 'string') {
				// Check if string represents a decimal number
				const numValue = Number(expression.Value);
				if (!isNaN(numValue) && expression.Value.includes('.')) {
					return PrimitiveType.Decimal;
				}
				return PrimitiveType.String;
			}
			if (typeof expression.Value === 'number') {
				// Check if number has decimal point
				if (Number.isInteger(expression.Value)) {
					return PrimitiveType.Integer;
				} else {
					return PrimitiveType.Decimal;
				}
			}
			if (typeof expression.Value === 'boolean') {
				return PrimitiveType.Boolean;
			}
		}
	}
	return undefined;
}

function getValueOption(valueOptions: ParameterValueOption[], value: unknown) {
	return valueOptions.find((valueOption) => String(valueOption.value) === String(value));
}

function getValueOptionDisplayLabel(valueOptions: ParameterValueOption[], value: unknown) {
	const option = getValueOption(valueOptions, value);
	if (!option) {
		return String(value);
	}

	const optionValue = String(option.value);
	return option.name === optionValue ? option.name : `${option.name} (${optionValue})`;
}

type ConstExpressionInputProps = {
	matchingPrimitiveTypes: PrimitiveType[];
	matchingEnum?: EnumMetadataModel;
	valueOptions?: ParameterValueOption[];
	allowCustomValues?: boolean;
	hasLoadedValueOptionsSource?: boolean;
	expression: ExpressionModel | undefined;
	onExpressionChange?: (expression: ExpressionModel) => void;
};

export default function ConstExpressionInput({
	matchingPrimitiveTypes,
	matchingEnum,
	valueOptions = [],
	allowCustomValues,
	hasLoadedValueOptionsSource,
	expression,
	onExpressionChange,
}: ConstExpressionInputProps) {
	// Filter to only include types we have UI for
	const supportedMatchingTypes = useMemo(() => matchingPrimitiveTypes.filter((type) => SUPPORTED_CONST_INPUT_TYPES.includes(type)), [matchingPrimitiveTypes]);

	// Group types by category
	const typeGroups = useMemo(() => {
		const group1 = [PrimitiveType.String, PrimitiveType.Integer, PrimitiveType.Decimal, PrimitiveType.Boolean];
		const group2 = [PrimitiveType.DateTime, PrimitiveType.Date, PrimitiveType.TimeSpan];
		const group3 = [PrimitiveType.Guid, PrimitiveType.Enum];

		return [
			supportedMatchingTypes.filter((type) => group1.includes(type)),
			supportedMatchingTypes.filter((type) => group2.includes(type)),
			supportedMatchingTypes.filter((type) => group3.includes(type)),
		].filter((group) => group.length > 0);
	}, [supportedMatchingTypes]);

	// When an enum is provided, prefer the first matching type (Enum or String); fall back to String if neither present
	const [valueType, setValueType] = useState<string | null>(
		matchingEnum
			? (supportedMatchingTypes.find((t) => t === PrimitiveType.Enum || t === PrimitiveType.String) ?? PrimitiveType.String)
			: (DetectValueType(expression) ?? supportedMatchingTypes[0] ?? null),
	);
	const [value, setValue] = useState<string | number | boolean | null>(expression?.Value ?? null);

	useEffect(() => {
		const newExpression: ConstExpressionModel = {
			Type: ExpressionTypes.Const,
			Value: value,
		};
		if (valueType && SAVE_CONST_VALUE_TYPES.includes(valueType as PrimitiveType)) {
			newExpression.ValueType = valueType as PrimitiveType;
		}
		onExpressionChange?.(newExpression);
	}, [valueType, value, onExpressionChange]);

	// Helper function to ensure we never pass null to TextField value prop
	const getStringValue = (val: string | number | boolean | null): string => {
		if (val === null || val === undefined) return '';
		return String(val);
	};
	const stringValue = getStringValue(value);
	const stringOptions = useMemo(() => valueOptions.map((option) => String(option.value)), [valueOptions]);
	const stringValueIsUnknown =
		stringValue !== ''
		&& hasLoadedValueOptionsSource
		&& valueOptions.length > 0
		&& !allowCustomValues
		&& !hasValueOption(valueOptions, stringValue);
	const selectableStringOptions = useMemo(() => {
		if (!stringValueIsUnknown) {
			return stringOptions;
		}

		return [...stringOptions, stringValue].sort((left, right) =>
			getValueOptionLabel(valueOptions, left).localeCompare(getValueOptionLabel(valueOptions, right), undefined, { sensitivity: 'base' }));
	}, [stringOptions, stringValue, stringValueIsUnknown, valueOptions]);

	// Specific helper for number TextField to ensure we return a string representation
	const getNumberValueAsString = (val: string | number | boolean | null): string => {
		if (val === null || val === undefined) return '';
		const numVal = Number(val);
		return isNaN(numVal) ? '' : String(numVal);
	};

	return (
		<Stack direction="column" spacing={2}>
			<Stack direction="column" spacing={1}>
				{typeGroups.map((group, groupIndex) => (
					<ToggleButtonGroup
						key={groupIndex}
						value={valueType}
						exclusive
						onChange={(_, newValue) => {
							if (newValue !== null) {
								setValueType(newValue);
								setValue(null);
							}
						}}
						fullWidth
						size="small"
						sx={{
							'& .MuiToggleButton-root': {
								py: 0.5,
								px: 1,
								fontSize: '0.75rem',
								textTransform: 'none',
							},
						}}
					>
						{group.map((type) => (
							<ToggleButton key={type} value={type}>
								<Stack direction="row" spacing={0.5} alignItems="center">
									{getIconForType(type)}
									<span>{(type === PrimitiveType.String || type === PrimitiveType.Enum) && matchingEnum ? matchingEnum.name : type}</span>
								</Stack>
							</ToggleButton>
						))}
					</ToggleButtonGroup>
				))}
			</Stack>

			{valueType && (
				<Stack direction="column" spacing={1}>
					{(valueType === PrimitiveType.String || valueType === PrimitiveType.Enum) && matchingEnum && (
						<RadioGroup name="enum-value" value={getStringValue(value)} onChange={(e) => setValue(e.target.value)}>
							{matchingEnum.items.map((item) => (
								<FormControlLabel key={item.id} value={item.name} control={<Radio />} label={item.name} />
							))}
						</RadioGroup>
					)}
					{valueType === PrimitiveType.String && !matchingEnum && valueOptions.length > 0 && (
						<Autocomplete
							disablePortal
							freeSolo={allowCustomValues ?? false}
							options={selectableStringOptions}
							value={stringValue}
							onChange={(_, selected) => setValue(selected ?? '')}
							onInputChange={(_, inputValue, reason) => {
								if (reason === 'input') {
									setValue(inputValue);
								}
							}}
							getOptionLabel={(option) => getValueOptionDisplayLabel(valueOptions, option)}
							renderOption={(props, option) => {
								const valueOption = getValueOption(valueOptions, option);
								return (
									<li {...props}>
										<Stack direction="row" spacing={1} alignItems="baseline" sx={{ width: '100%', minWidth: 0 }}>
											<Typography component="span" variant="body2">
												{getValueOptionLabel(valueOptions, option)}
											</Typography>
											<Typography component="span" variant="caption" color="text.secondary" sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
												{valueOption ? String(valueOption.value) : String(option)}
											</Typography>
										</Stack>
									</li>
								);
							}}
							renderInput={(params) => (
								<TextField
									{...params}
									variant="outlined"
									fullWidth
									error={stringValueIsUnknown}
									helperText={stringValueIsUnknown ? 'This value is no longer available from the selected service connection.' : undefined}
								/>
							)}
						/>
					)}
					{valueType === PrimitiveType.String && !matchingEnum && valueOptions.length === 0 && (
						<TextField key={valueType} variant="outlined" fullWidth value={getStringValue(value)} onChange={(e) => setValue(e.target.value ?? '')} />
					)}
					{valueType === PrimitiveType.Integer && (
						<TextField
							key={valueType}
							variant="outlined"
							fullWidth
							type="number"
							value={getNumberValueAsString(value)}
							onChange={(e) => {
								const inputValue = e.target.value;
								if (inputValue === '') {
									setValue(null);
								} else {
									const numValue = Number(inputValue);
									if (!isNaN(numValue)) {
										setValue(numValue);
									}
								}
							}}
						/>
					)}
					{valueType === PrimitiveType.Decimal && (
						<TextField
							key={valueType}
							variant="outlined"
							fullWidth
							type="number"
							inputProps={{ step: '0.01' }}
							value={getNumberValueAsString(value)}
							onChange={(e) => {
								const inputValue = e.target.value;
								if (inputValue === '') {
									setValue(null);
								} else {
									const numValue = Number(inputValue);
									if (!isNaN(numValue)) {
										setValue(numValue);
									}
								}
							}}
						/>
					)}
					{valueType === PrimitiveType.Boolean && (
						<Stack direction="row" justifyContent="center">
							<FormControlLabel
								control={<Switch checked={value === true} onChange={(e) => setValue(e.target.checked ? true : false)} />}
								label={value === true ? 'True' : 'False'}
							/>
						</Stack>
					)}
					{valueType === PrimitiveType.Guid && (
						<GuidInput key={valueType} value={getStringValue(value)} onValueChange={(val) => setValue(val)} variant="outlined" />
					)}
					{valueType === PrimitiveType.TimeSpan && (
						<Stack direction="row" justifyContent="center" sx={{ width: '100%' }}>
							<Stack direction="row" sx={{ display: 'inline-flex' }}>
								<TimeSpanInput key={valueType} value={getStringValue(value)} onValueChange={(val) => setValue(val)} optional={false} />
							</Stack>
						</Stack>
					)}
					{valueType === PrimitiveType.Date && (
						<TextField key={valueType} variant="outlined" fullWidth type="date" value={getStringValue(value)} onChange={(e) => setValue(e.target.value)} />
					)}
					{valueType === PrimitiveType.DateTime && (
						<TextField
							key={valueType}
							variant="outlined"
							fullWidth
							type="datetime-local"
							value={getStringValue(value)}
							onChange={(e) => setValue(e.target.value)}
						/>
					)}
				</Stack>
			)}
		</Stack>
	);
}
